import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'
import { supabase, type Store, type Trip, type TripShopper } from '../lib/supabase'
import { learnTrip, pickedOrders, type PickedItem } from '../lib/storeRoute'
import { joinWith, openTripOf, roleOf, tripNoticeFor, type TripRole } from '../lib/sharedTrip'
import { TRIPS_KEY } from './useTrips'

// Shopping mode's data (037, 038, 045): the household's stores with their
// learned routes, and its one open trip with who is shopping in it.
//
// A household has at most one open trip (a trip started in the last
// STALE_BASKET_MS, not finished), read from the server, so a trip started on a
// watch or another phone shows here: this person's carries on as Continue
// shopping, someone else's is joined (lib/sharedTrip.ts). Starting, joining,
// leaving and finishing go through the database's functions, which serialise
// on the household — two Starts are one trip, two Finishes finish it once.
// The phone shows each change at once and the answer settles it; Realtime on
// trips and trip_shoppers keeps every phone in step. The same read finds a
// trip that ended elsewhere: someone else's finish is a note here (Keep
// shopping), and this person's own finish on the watch asks its cost once
// (046). Route writes are best effort.

export const STORES_KEY = (householdId: string) => ['stores', householdId] as const
export const SHARED_TRIPS_KEY = (householdId: string) => ['shared-trips', householdId] as const
const LAST_STORE_STORAGE = 'matlist.lastStore'
const SEEN_STORAGE = 'matlist.seenTrips'
const TRIP_COLUMNS = 'id, household_id, store_id, started_by, started_at, finished_at, finished_by, finished_on, items, cost, currency, updated_at'

interface SharedTrips {
  rows: Trip[]
  shoppers: TripShopper[]
}

/** What Finish trip answers: the trip it finished, when, and whether someone else had already. */
export interface FinishedTrip {
  tripId: string
  finishedAt: string
  already: boolean
}

export interface OpenTrip {
  id: string
  storeId: string | null
  startedAt: string
}

const byName = (a: Store, b: Store) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
const sameName = (a: string, b: string) =>
  a.trim().replace(/\s+/g, ' ').toLocaleLowerCase() === b.trim().replace(/\s+/g, ' ').toLocaleLowerCase()

function readList(key: string): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? '[]')
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

function readLastStore(): string | null {
  try { return localStorage.getItem(LAST_STORE_STORAGE) } catch { return null }
}

/** The household's stores, by name — shopping mode's picker and the stats page. */
export function useStores(householdId: string): Store[] {
  const query = useQuery({
    queryKey: STORES_KEY(householdId),
    enabled: !!householdId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stores').select('id, household_id, name, route, created_at, updated_at')
        .eq('household_id', householdId)
      if (error) throw error
      return (data as Store[]).sort(byName)
    },
  })
  return useMemo(() => query.data ?? [], [query.data])
}

/** "What did it cost?" for a trip — null clears it. The trip summary's writer, wherever it opens. */
export function useTripCost(householdId: string) {
  const qc = useQueryClient()
  return useCallback(async (tripId: string, cost: number | null, currency: string | null): Promise<boolean> => {
    const { error } = await supabase.from('trips')
      .update({ cost, currency: cost === null ? null : currency })
      .eq('id', tripId)
    if (!error) void qc.invalidateQueries({ queryKey: TRIPS_KEY(householdId) })
    return !error
  }, [qc, householdId])
}

export interface UseShoppingOptions {
  /**
   * The household has Plus (lib/billing.ts). Shopping works on Free too, but
   * only Plus teaches a store its walking order — on Free a store's learned
   * route is kept as it was (042_free_plus.sql freezes it server-side too, so
   * this only saves the request).
   */
  plus?: boolean
}

export function useShopping(householdId: string, memberId: string | null, options: UseShoppingOptions = {}) {
  const qc = useQueryClient()
  const key = STORES_KEY(householdId)
  const tripsKey = useMemo(() => SHARED_TRIPS_KEY(householdId), [householdId])
  const { plus = true } = options
  const [lastStoreId, setLastStoreId] = useState<string | null>(readLastStore)
  const [seen, setSeen] = useState<string[]>(() => readList(SEEN_STORAGE))
  const stores = useStores(householdId)

  // The household's latest trips and who shopped in them: the open one, and
  // ones that ended elsewhere.
  const tripsQuery = useQuery({
    queryKey: tripsKey,
    enabled: !!householdId && !!memberId,
    queryFn: async (): Promise<SharedTrips> => {
      const { data, error } = await supabase
        .from('trips').select(TRIP_COLUMNS)
        .eq('household_id', householdId)
        .order('started_at', { ascending: false })
        .limit(10)
      if (error) throw error
      const rows = data as Trip[]
      if (!rows.length) return { rows, shoppers: [] }
      const { data: shoppers, error: shoppersError } = await supabase
        .from('trip_shoppers').select('trip_id, member_id, household_id, joined_at, left_at')
        .in('trip_id', rows.map(t => t.id))
      if (shoppersError) throw shoppersError
      return { rows, shoppers: shoppers as TripShopper[] }
    },
  })
  const rows = useMemo(() => tripsQuery.data?.rows ?? [], [tripsQuery.data])
  const shoppers = useMemo(() => tripsQuery.data?.shoppers ?? [], [tripsQuery.data])
  // "Now" is when the rows were read (or last written here): render stays pure,
  // and the read is fresh whenever the app comes back to the foreground.
  const readAt = tripsQuery.dataUpdatedAt

  // Someone started, joined, left or finished, on a phone or a watch: read again.
  // App and the list each hold this hook: each its own topic (supabase-js hands
  // back an already-subscribed channel for a topic in use).
  const instance = useId()
  useEffect(() => {
    if (!householdId || !memberId) return
    const refresh = () => { void qc.invalidateQueries({ queryKey: tripsKey }) }
    const filter = `household_id=eq.${householdId}`
    const channel = supabase.channel(`shared-trips:${householdId}:${instance}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'trips', filter }, refresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'trip_shoppers', filter }, refresh)
      .subscribe()
    return () => { void supabase.removeChannel(channel) }
  }, [householdId, memberId, qc, tripsKey, instance])

  const openRow = useMemo(() => openTripOf(rows, readAt), [rows, readAt])
  const trip = useMemo((): OpenTrip | null =>
    openRow ? { id: openRow.id, storeId: openRow.store_id, startedAt: openRow.started_at } : null, [openRow])
  const role: TripRole = useMemo(() => roleOf(openRow, shoppers, memberId), [openRow, shoppers, memberId])
  const joinMemberId = useMemo(() => (role === 'join' ? joinWith(openRow, shoppers, memberId) : null), [role, openRow, shoppers, memberId])
  const others = useMemo(() => (openRow
    ? shoppers.filter(s => s.trip_id === openRow.id && !s.left_at && s.member_id !== memberId).length
    : 0), [openRow, shoppers, memberId])

  const notice = useMemo(
    () => tripNoticeFor(rows, shoppers, memberId, seen, readAt), [rows, shoppers, memberId, seen, readAt])

  /** Changes the cached trips at once; a read still in flight is cancelled first, or its older answer would undo it. */
  const patch = useCallback((change: (old: SharedTrips) => SharedTrips) => {
    void qc.cancelQueries({ queryKey: tripsKey })
    qc.setQueryData<SharedTrips>(tripsKey, old => change(old ?? { rows: [], shoppers: [] }))
  }, [qc, tripsKey])
  // The answer settles it: start_trip's trip replaces the one shown (a lost
  // race is a join: its id is the other one's). A failure reads the truth
  // again; everyone else's changes come in through Realtime.
  const settle = useCallback(({ data, error }: { data: unknown; error: unknown }, shown?: string) => {
    if (error) { void qc.invalidateQueries({ queryKey: tripsKey }); return }
    const answer = data as { trip?: Trip } | null
    const real = answer?.trip
    if (!real || !shown) return
    patch(old => ({
      rows: [real, ...old.rows.filter(t => t.id !== shown && t.id !== real.id)],
      shoppers: old.shoppers.map(x => (x.trip_id === shown ? { ...x, trip_id: real.id } : x)),
    }))
  }, [qc, tripsKey, patch])
  const me = useCallback((tripId: string): TripShopper => ({
    trip_id: tripId, member_id: memberId ?? '', household_id: householdId, joined_at: new Date().toISOString(), left_at: null,
  }), [memberId, householdId])

  const rememberStore = useCallback((storeId: string | null) => {
    setLastStoreId(storeId)
    try {
      if (storeId) localStorage.setItem(LAST_STORE_STORAGE, storeId)
      else localStorage.removeItem(LAST_STORE_STORAGE)
    } catch { /* private mode */ }
  }, [])

  /** Adds a store, or hands back the one already called that (case and spacing aside). */
  const addStore = useCallback(async (name: string): Promise<Store | null> => {
    const clean = name.trim().replace(/\s+/g, ' ').slice(0, 40)
    if (!clean) return null
    const current = qc.getQueryData<Store[]>(key) ?? []
    const existing = current.find(s => sameName(s.name, clean))
    if (existing) return existing
    const now = new Date().toISOString()
    const store: Store = { id: uuid(), household_id: householdId, name: clean, route: [], created_at: now, updated_at: now }
    qc.setQueryData<Store[]>(key, old => [...(old ?? []), store].sort(byName))
    const { error } = await supabase.from('stores').insert({ id: store.id, household_id: householdId, name: clean })
    if (error) {
      qc.setQueryData<Store[]>(key, old => (old ?? []).filter(s => s.id !== store.id))
      // Someone else added it a moment ago: use theirs.
      await qc.invalidateQueries({ queryKey: key })
      return (qc.getQueryData<Store[]>(key) ?? []).find(s => sameName(s.name, clean)) ?? null
    }
    return store
  }, [qc, key, householdId])

  /**
   * Start shopping at a store (or nowhere in particular). This person's open
   * trip moves there instead; someone else's is joined (`join`), never moved.
   */
  const start = useCallback((storeId: string | null) => {
    rememberStore(storeId)
    if (openRow && role === 'join') return
    if (openRow) {
      if (openRow.store_id === storeId) return
      patch(old => ({ ...old, rows: old.rows.map(t => (t.id === openRow.id ? { ...t, store_id: storeId } : t)) }))
    } else {
      const now = new Date().toISOString()
      const row: Trip = {
        id: uuid(), household_id: householdId, store_id: storeId, started_by: memberId, started_at: now,
        finished_at: null, finished_by: null, finished_on: null, items: null, cost: null, currency: null, updated_at: '',
      }
      patch(old => ({ rows: [row, ...old.rows], shoppers: [me(row.id), ...old.shoppers] }))
      void supabase.rpc('start_trip', { p_id: row.id, p_store: storeId }).then(r => settle(r, row.id))
      return
    }
    void supabase.rpc('start_trip', { p_id: openRow.id, p_store: storeId }).then(r => settle(r, openRow.id))
  }, [openRow, role, patch, settle, me, rememberStore, householdId, memberId])

  /** Join the trip someone else is on: this person is in it at once. */
  const join = useCallback(() => {
    if (!openRow) return
    patch(old => ({ ...old, shoppers: [...old.shoppers.filter(s => !(s.trip_id === openRow.id && s.member_id === memberId)), me(openRow.id)] }))
    void supabase.rpc('start_trip', { p_id: uuid(), p_store: null }).then(r => settle(r, openRow.id))
  }, [openRow, patch, settle, me, memberId])

  /** Leave the trip without finishing it: it stays open for the others. */
  const leave = useCallback(() => {
    if (!openRow) return
    const at = new Date().toISOString()
    patch(old => ({ ...old, shoppers: old.shoppers.map(s => (s.trip_id === openRow.id && s.member_id === memberId ? { ...s, left_at: at } : s)) }))
    void supabase.rpc('leave_trip', { p_trip: openRow.id }).then(r => settle(r))
  }, [openRow, patch, settle, memberId])

  /**
   * Finish trip, for everyone: the database finishes the trip and takes the
   * household's basket off the list in one go. Someone else may have got
   * there first (`already`): theirs stands, and the store learns nothing
   * twice. Otherwise the store learns each shopper's own walk through it
   * (pickedOrders: two people's ticks interleaved are no route). Throws when
   * it could not finish, so the basket goes back.
   */
  const finish = useCallback(async (bought: PickedItem[]): Promise<FinishedTrip> => {
    const { data, error } = await supabase.rpc('finish_trip', { p_trip: openRow?.id ?? null, p_on: 'phone' })
    if (error || !data) throw error ?? new Error('finish_trip answered nothing')
    const answer = data as { trip: Trip; already: boolean }
    const done = answer.trip
    patch(old => ({ ...old, rows: [done, ...old.rows.filter(t => t.id !== done.id)] }))
    void qc.invalidateQueries({ queryKey: TRIPS_KEY(householdId) })
    const store = !answer.already && done.store_id ? (qc.getQueryData<Store[]>(key) ?? []).find(s => s.id === done.store_id) : undefined
    if (store && plus) {
      const route = learnTrip(store.route, pickedOrders(bought))
      qc.setQueryData<Store[]>(key, old => (old ?? []).map(s => (s.id === store.id ? { ...s, route } : s)))
      void supabase.from('stores').update({ route }).eq('id', store.id).then(() => undefined)
    }
    return { tripId: done.id, finishedAt: done.finished_at ?? new Date().toISOString(), already: answer.already }
  }, [openRow, patch, qc, householdId, key, plus])

  /** A trip's note has been shown here: never again. */
  const markSeen = useCallback((id: string) => {
    setSeen(old => {
      const next = [id, ...old.filter(x => x !== id)].slice(0, 20)
      try { localStorage.setItem(SEEN_STORAGE, JSON.stringify(next)) } catch { /* private mode */ }
      return next
    })
  }, [])

  const storeOf = useCallback((id: string | null) => (id ? stores.find(s => s.id === id) ?? null : null), [stores])

  return {
    stores,
    /** The household's open trip, whoever started it. */
    trip,
    /** What Start shopping does for this person (lib/sharedTrip.ts). */
    role,
    /** Who to name on "Join …" (a member id), while the trip is someone else's. */
    joinMemberId,
    /** How many others are shopping in the open trip. */
    others,
    start,
    join,
    leave,
    finish,
    /** Someone else finished the trip this person was shopping in: the note, once (lib/sharedTrip.ts). */
    notice,
    markSeen,
    loaded: !memberId || tripsQuery.isFetched,
    /** The trip's store, or the one last shopped at on this phone, for the picker's first choice. */
    suggestedStoreId: trip?.storeId ?? (lastStoreId && stores.some(s => s.id === lastStoreId) ? lastStoreId : null),
    storeOf,
    addStore,
  }
}
